import { motion } from "framer-motion";
import { ShieldCheck, Sparkles, TrendingUp, Star } from "lucide-react";
import { MultiStepLeadForm } from "../lead/MultiStepLeadForm";
import { HERO } from "../../constants/testIds";

export function Hero() {
	return (
		<section className="relative overflow-hidden pt-28 pb-16 sm:pt-32 lg:pt-40 lg:pb-24">
			<div className="absolute inset-0 bg-grid pointer-events-none" />
			<div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-emerald-400/20 blur-3xl pointer-events-none" />
			<div className="absolute top-40 -right-24 h-96 w-96 rounded-full bg-teal-400/10 blur-3xl pointer-events-none" />

			<div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
				<div>
					<motion.div
						initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
						className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300"
					>
						<Sparkles className="h-3.5 w-3.5" /> India's School Growth Partner
					</motion.div>

					<motion.h1
						data-testid={HERO.headline}
						initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
						className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-[1.08]"
					>
						Turn Your School Into a{" "}
						<span className="bg-gradient-to-r from-emerald-600 via-teal-500 to-emerald-600 bg-clip-text text-transparent">
							Growth Engine.
						</span>
					</motion.h1>

					<motion.p
						initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
						className="mt-5 text-lg sm:text-xl text-slate-600 dark:text-slate-300 max-w-xl"
					>
						More visibility. More enquiries. More admissions. More growth.
					</motion.p>

					<motion.div
						initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
						className="mt-7 flex flex-wrap gap-3"
					>
						<span data-testid={HERO.badgeAdmission}
							className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-amber-500/15 to-amber-500/10 border border-amber-500/30 px-4 py-2 text-sm font-semibold text-amber-700 dark:text-amber-300">
							<ShieldCheck className="h-4 w-4" /> 100% Admission Assurance*
						</span>
						<span data-testid={HERO.badgePreschool}
							className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-500/15 to-emerald-500/10 border border-emerald-500/30 px-4 py-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
							<TrendingUp className="h-4 w-4" /> 50 Admissions Assured for Preschool Launch*
						</span>
					</motion.div>

					<motion.div
						initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
						className="mt-8 flex items-center gap-4"
					>
						<div className="flex -space-x-2">
							{[1,2,3,4].map((i) => (
								<span key={i} className="h-9 w-9 rounded-full border-2 border-white dark:border-slate-900 bg-gradient-to-br from-emerald-400 to-teal-500" />
							))}
						</div>
						<div className="text-sm">
							<div className="flex items-center gap-1 text-amber-500">
								{[...Array(5)].map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
							</div>
							<p className="text-slate-500 dark:text-slate-400">Trusted by 250+ schools across India</p>
						</div>
					</motion.div>
				</div>

				{/* Hero form card */}
				<motion.div
					initial={{ opacity: 0, scale: 0.96, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ delay: 0.2 }}
					id="consultation"
					className="relative rounded-3xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl shadow-slate-900/10 p-6 sm:p-8"
				>
					<div className="mb-5">
						<p className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 dark:text-emerald-400">
							Free Growth Consultation
						</p>
						<h3 className="font-heading text-2xl font-extrabold text-slate-900 dark:text-white mt-1">
							Get your school's growth plan
						</h3>
					</div>
					<div id="hero-form" data-testid={HERO.consultationCta}>
						<MultiStepLeadForm sourcePage="Homepage" embedded />
					</div>
				</motion.div>
			</div>
		</section>
	);
}
